import { useState } from 'react';
import { categories, decadeOptions, sortOptions } from '@/mocks/categories';
import type { DecadeFilter, SortFilter } from '@/utils/movies';

export interface FilterBarProps {
  genre: string;
  decade: DecadeFilter;
  sort: SortFilter;
  onGenreChange: (value: string) => void;
  onDecadeChange: (value: DecadeFilter) => void;
  onSortChange: (value: SortFilter) => void;
}

const genreOptions = [{ value: 'all', label: '全部类型' }, ...categories.map((c) => ({ value: c.slug, label: c.name }))];

function Segment({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition cursor-pointer ${
              active
                ? 'bg-accent-500 text-background-50 dark:text-foreground-950'
                : 'text-foreground-700 hover:bg-background-200/70'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default function FilterBar({ genre, decade, sort, onGenreChange, onDecadeChange, onSortChange }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="mb-4 flex items-center justify-between md:hidden">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2 text-sm font-medium text-foreground-800 transition hover:bg-background-200/70 cursor-pointer"
        >
          <i className="ri-filter-3-line"></i>
          筛选与排序
          <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
        </button>
        <span className="text-xs text-foreground-500">
          {genre === 'all' ? '全部类型' : genre}
        </span>
      </div>

      <div className={`${open ? 'block anim-fade-in' : 'hidden'} space-y-4 md:block`}>
        <div className="space-y-4 md:flex md:flex-wrap md:items-center md:gap-x-6 md:gap-y-3 md:space-y-0">
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-xs font-semibold text-foreground-500">类型</span>
            <Segment options={genreOptions} value={genre} onChange={onGenreChange} />
          </div>
        </div>
        <div className="md:flex md:flex-wrap md:items-center md:gap-x-6 md:gap-y-3 md:space-y-0">
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-xs font-semibold text-foreground-500">年代</span>
            <Segment options={decadeOptions} value={decade} onChange={(v) => onDecadeChange(v as DecadeFilter)} />
          </div>
        </div>
        <div className="md:flex md:flex-wrap md:items-center md:gap-x-6 md:gap-y-3 md:space-y-0">
          <div className="flex items-center gap-3">
            <span className="w-12 shrink-0 text-xs font-semibold text-foreground-500">排序</span>
            <Segment options={sortOptions} value={sort} onChange={(v) => onSortChange(v as SortFilter)} />
          </div>
        </div>
      </div>
    </div>
  );
}